// ─────────────────────────────────────────────────────────────────────────────
// Pricing rules
//   per_unit_serves — { rate, serves, unitLabel }
//                      qty = ceil(guests / serves)   (serves=1 → qty=guests)
//                      total = qty × rate
//   qty_offset      — { offset, rate=0 }             zero-line by default
//                      qty = guests + offset
//   inclusive       — { }                            qty=1, rate=0, label "Inclusive"
//   flat            — { flatPrice }                  qty=1, total=flatPrice
//
// Optional surcharge (any rule):
//   surcharge: { type: 'lump'|'per_person', amount, condition?: { thresholdGuests, op:'lt'|'gte' } }
//   - 'lump'        — adds `amount` once
//   - 'per_person'  — adds `amount × guests`
//   - condition omitted → always applied
//
// Line-level overrides supported by calcLine via `override`:
//   qty, unitPrice, lineExtra, profitPct, notes
// ─────────────────────────────────────────────────────────────────────────────

export const CATEGORIES = [
  'Main Course',
  'Starters',
  'Desserts',
  'Beverages',
  'Stalls',
  'Inclusive',
  'Service',
]

// All hard-coded inventory entries below default to serviceType: 'Catering'
// (applied once at the bottom of the file). Photography inventory will be
// added later as separate entries with serviceType: 'Photography'.
const _CATERING_ITEMS = [
  // Main Course
  {
    id: 'biriyani-chicken',
    name: 'Chicken Biriyani',
    category: 'Main Course',
    rule: 'per_unit_serves',
    unitLabel: 'Kg',
    rate: 1200,
    serves: 8,
  },
  {
    id: 'biriyani-mutton',
    name: 'Mutton Biriyani',
    category: 'Main Course',
    rule: 'per_unit_serves',
    unitLabel: 'Kg',
    rate: 2400,
    serves: 8,
  },

  // Starters & Sides — sit after Biriyani, in the order:
  // Chicken 65 → Idiyappam → Pepper Chicken
  {
    id: 'chicken-65',
    name: 'Chicken 65',
    category: 'Starters',
    rule: 'per_unit_serves',
    unitLabel: 'Kg',
    rate: 600,
    serves: 12,
  },
  {
    id: 'idiyappam',
    name: 'Idiyappam',
    category: 'Starters',
    rule: 'per_unit_serves',
    unitLabel: 'Nos',
    rate: 5,
    serves: 1,
  },
  {
    id: 'pepper-chicken',
    name: 'Pepper Chicken',
    category: 'Starters',
    rule: 'per_unit_serves',
    unitLabel: 'Kg',
    rate: 600,
    serves: 12,
  },

  // Desserts
  {
    id: 'kheer',
    name: 'Kheer / Phirni',
    category: 'Desserts',
    rule: 'per_unit_serves',
    unitLabel: 'Ltr',
    rate: 300,
    serves: 15,
  },

  // Beverages
  {
    id: 'welcome-juice',
    name: 'Welcome Juice',
    category: 'Beverages',
    rule: 'per_unit_serves',
    unitLabel: 'Nos',
    rate: 10,
    serves: 1,
    description: 'Pineapple & Watermelon',
    surcharge: {
      type: 'lump',
      amount: 1000,
      condition: { thresholdGuests: 300, op: 'lt' },
    },
    note: 'If guests < 300 → + ₹1000 surcharge',
  },
  {
    id: 'rosemilk',
    name: 'Rosemilk',
    category: 'Beverages',
    rule: 'per_unit_serves',
    unitLabel: 'Nos',
    rate: 10,
    serves: 1,
    surcharge: {
      type: 'lump',
      amount: 1000,
      condition: { thresholdGuests: 300, op: 'lt' },
    },
    note: 'If guests < 300 → + ₹1000 surcharge',
  },

  // Stalls — live counters & specialty stations
  {
    id: 'fruit-salad-4',
    name: 'Fruit Salad (4 Type)',
    category: 'Stalls',
    rule: 'per_unit_serves',
    unitLabel: 'Nos',
    rate: 10,
    serves: 1,
    description: 'Apple, Pineapple, Watermelon, Papaya',
  },
  {
    id: 'fruit-salad-5',
    name: 'Fruit Salad (5 Type)',
    category: 'Stalls',
    rule: 'per_unit_serves',
    unitLabel: 'Nos',
    rate: 12,
    serves: 1,
    description: 'Apple, Pineapple, Watermelon, Papaya, Grapes',
  },
  {
    id: 'icecream',
    name: 'Icecream',
    category: 'Stalls',
    rule: 'per_unit_serves',
    unitLabel: 'Ltr',
    rate: 280,
    serves: 35,
    description: 'Vanilla & Strawberry',
    surcharge: { type: 'lump', amount: 500 },
    note: '+ ₹500 service person (always)',
  },
  {
    id: 'icecream-premium',
    name: 'Icecream',
    category: 'Stalls',
    rule: 'per_unit_serves',
    unitLabel: 'Ltr',
    rate: 380,
    serves: 35,
    description: 'Butterscotch',
    surcharge: { type: 'lump', amount: 500 },
    note: '+ ₹500 service person (always)',
  },
  {
    id: 'live-coffee-stall',
    name: 'Live Coffee Stall',
    category: 'Stalls',
    rule: 'per_unit_serves',
    unitLabel: 'Nos',
    rate: 15,
    serves: 1,
  },
  {
    id: 'beeda',
    name: 'Beeda Stall',
    category: 'Stalls',
    rule: 'per_unit_serves',
    unitLabel: 'Nos',
    rate: 6,
    serves: 1,
    surcharge: {
      type: 'per_person',
      amount: 2,
      condition: { thresholdGuests: 300, op: 'lt' },
    },
    note: 'If guests < 300 → + ₹2 / person',
  },
  {
    id: 'panipuri',
    name: 'Panipuri',
    category: 'Stalls',
    rule: 'per_unit_serves',
    unitLabel: 'Nos',
    rate: 10,
    serves: 1,
  },
  {
    id: 'spring-potato-roll',
    name: 'Spring Potato Roll',
    category: 'Stalls',
    rule: 'per_unit_serves',
    unitLabel: 'Nos',
    rate: 25,
    serves: 1,
  },
  {
    id: 'sweet-corn',
    name: 'Sweet Corn',
    category: 'Stalls',
    rule: 'per_unit_serves',
    unitLabel: 'Nos',
    rate: 10,
    serves: 1,
  },
  {
    id: 'popcorn-cotton-candy',
    name: 'Popcorn & Cotton Candy Stall',
    category: 'Stalls',
    rule: 'flat',
    flatPrice: 3000,
    unitLabel: 'lump-sum',
  },
  {
    id: 'chocolate-fountain',
    name: 'Chocolate Fountain',
    category: 'Stalls',
    rule: 'flat',
    flatPrice: 2000,
    unitLabel: 'lump-sum',
  },

  // Inclusive (zero-line)
  {
    id: 'raitha',
    name: 'Raitha',
    category: 'Inclusive',
    rule: 'inclusive',
    unitLabel: 'Inclusive',
  },
  {
    id: 'bread-halwa',
    name: 'Bread Halwa',
    category: 'Inclusive',
    rule: 'inclusive',
    unitLabel: 'Inclusive',
  },
  {
    id: 'paper-roll',
    name: 'Paper Roll',
    category: 'Inclusive',
    rule: 'inclusive',
    unitLabel: 'Inclusive',
  },
  {
    id: 'brinjal',
    name: 'Brinjal',
    category: 'Inclusive',
    rule: 'inclusive',
    unitLabel: 'Inclusive',
  },
  {
    id: 'banana-leaf',
    name: 'Banana Leaf',
    category: 'Inclusive',
    rule: 'qty_offset',
    unitLabel: 'Nos',
    rate: 0,
    offset: 50,
    note: 'Zero line · Qty = Guests + 50',
  },
  {
    id: 'water-bottle',
    name: 'Water Bottle',
    category: 'Inclusive',
    rule: 'qty_offset',
    unitLabel: 'Nos',
    rate: 0,
    offset: 50,
    note: 'Zero line · Qty = Guests + 50',
  },

  // Catering Service — last item in the catalog.
  {
    id: 'catering-service',
    name: 'Catering Service',
    category: 'Service',
    rule: 'package_qty',
    unitLabel: 'Nos',
    rate: 500,
    qtyByPackage: { Starter: 5, Basic: 6, Premium: 7, Custom: 6 },
    note: '₹500/Nos · Qty by package: Starter 5 · Basic 6 · Premium 7 · Custom 6',
  },
]

const _PHOTOGRAPHY_ITEMS = [
  // Photography packages
  {
    id: 'photo-trad-basic',
    name: 'Photography',
    category: 'Photography',
    rule: 'flat',
    unitLabel: 'package',
    flatPrice: 9000,
    description:
      'Traditional Photography\nUnlimited Photos\n1 Basic Album\n\nComplimentary\n1 Photo Frame',
  },
  {
    id: 'photo-trad-premium-10k',
    name: 'Photography',
    category: 'Photography',
    rule: 'flat',
    unitLabel: 'package',
    flatPrice: 10000,
    description:
      'Traditional Photography\nUnlimited Photos\n1 Premium Album\n\nComplimentary\n1 Photo Frame',
  },
  {
    id: 'photo-trad-premium-11k',
    name: 'Photography',
    category: 'Photography',
    rule: 'flat',
    unitLabel: 'package',
    flatPrice: 11000,
    description:
      'Traditional Photography\nUnlimited Photos\n1 Premium Album\n\nComplimentary\n1 Photo Frame',
  },
  {
    id: 'photo-trad-premium-13k',
    name: 'Photography',
    category: 'Photography',
    rule: 'flat',
    unitLabel: 'package',
    flatPrice: 13000,
    description:
      'Traditional Photography\nUnlimited Photos\n1 Premium Album\n\nComplimentary\n1 Photo Frame',
  },
  {
    id: 'photo-candid-deluxe',
    name: 'Photography',
    category: 'Photography',
    rule: 'flat',
    unitLabel: 'package',
    flatPrice: 20000,
    description:
      'Traditional Photography\nCandid Photography\nUnlimited Photos\n1 Premium Album with Box\n\nComplimentary\n1 Photo Frame\n1 Calendar',
  },

  // Videography
  {
    id: 'video-trad',
    name: 'Videography',
    category: 'Videography',
    rule: 'flat',
    unitLabel: 'package',
    flatPrice: 10000,
    description:
      'Traditional Videography\n1 Edited Traditional Video\n\nComplimentary\n1 PenDrive',
  },

  // Combo (photo + video)
  {
    id: 'photo-video-combo',
    name: 'Photography',
    category: 'Combo',
    rule: 'flat',
    unitLabel: 'package',
    flatPrice: 25000,
    description:
      'Traditional Photography\nTraditional Videography\nUnlimited Photos\n1 Premium Album with Box\n1 Edited Traditional Video\n2 Mins Montage Video\n\nComplimentary\n1 Photo Frame\n1 Calendar\n1 PenDrive',
  },
]

export const INVENTORY = [
  ..._CATERING_ITEMS.map((i) => ({ serviceType: 'Catering', ...i })),
  ..._PHOTOGRAPHY_ITEMS.map((i) => ({ serviceType: 'Photography', ...i })),
]

export const SERVICE_TYPES = ['Catering', 'Photography']

// Default item selection per package (by item id).
// Common items appear in ALL packages (Starter / Basic / Premium / Custom).
const CATERING_COMMON = [
  'raitha',
  'bread-halwa',
  'paper-roll',
  'brinjal',
  'banana-leaf',
  'water-bottle',
  'catering-service',
]
export const PACKAGE_PRESETS = {
  Catering: {
    Starter: ['biriyani-chicken', ...CATERING_COMMON],
    Basic: [
      'biriyani-chicken',
      'chicken-65',
      'icecream', // ₹280/Ltr — Vanilla & Strawberry
      'welcome-juice',
      'beeda',
      ...CATERING_COMMON,
    ],
    Premium: [
      'biriyani-chicken',
      'chicken-65',
      'kheer',
      'idiyappam',
      'pepper-chicken',
      'icecream-premium', // ₹380/Ltr — Butterscotch
      'welcome-juice',
      'beeda',
      'fruit-salad-5',
      'live-coffee-stall',
      'popcorn-cotton-candy',
      ...CATERING_COMMON,
    ],
    Custom: ['biriyani-chicken', 'chicken-65', ...CATERING_COMMON],
  },
  Photography: {
    Starter: null,
    Basic: null,
    Premium: null,
    Custom: null,
  },
}

// calcLine returns full breakdown for a single item.
// ctx: { packageName } — used by package-aware rules (e.g. catering service)
// Output: { qty, unitPrice, unitLabel, baseAmount, surcharge, surchargeNote,
//           lineExtra, profitPct, lineProfit, total }
export function calcLine(item, guests, override = {}, ctx = {}) {
  const g = Math.max(0, Number(guests) || 0)
  let qty = 0
  let unitPrice = 0
  let unitLabel = item.unitLabel || 'unit'

  switch (item.rule) {
    case 'per_unit_serves':
      qty = item.serves > 0 ? Math.ceil(g / item.serves) : 0
      unitPrice = Number(item.rate) || 0
      break
    case 'package_qty': {
      const pkg = ctx?.packageName || 'Custom'
      const map = item.qtyByPackage || {}
      qty = Number(map[pkg] ?? map.Custom ?? 0)
      unitPrice = Number(item.rate) || 0
      break
    }
    case 'qty_offset':
      qty = g + (Number(item.offset) || 0)
      unitPrice = Number(item.rate) || 0
      break
    case 'inclusive':
      qty = 1
      unitPrice = 0
      unitLabel = 'Inclusive'
      break
    case 'flat':
      qty = 1
      unitPrice = Number(item.flatPrice) || 0
      unitLabel = item.unitLabel || 'lump-sum'
      break
    default:
      qty = 0
      unitPrice = 0
  }

  if (override.qty !== undefined && override.qty !== '') qty = Number(override.qty) || 0
  if (override.unitPrice !== undefined && override.unitPrice !== '')
    unitPrice = Number(override.unitPrice) || 0

  const baseAmount = Math.round(qty * unitPrice)

  // Surcharge
  let surcharge = 0
  let surchargeNote = ''
  if (item.surcharge) {
    const cond = item.surcharge.condition
    let apply = true
    if (cond) {
      if (cond.op === 'lt') apply = g < Number(cond.thresholdGuests)
      else if (cond.op === 'gte') apply = g >= Number(cond.thresholdGuests)
      else if (cond.op === 'lte') apply = g <= Number(cond.thresholdGuests)
      else if (cond.op === 'gt') apply = g > Number(cond.thresholdGuests)
    }
    if (apply) {
      const amt = Number(item.surcharge.amount) || 0
      if (item.surcharge.type === 'lump') {
        surcharge = amt
        surchargeNote = `+ ₹${amt.toLocaleString('en-IN')} surcharge`
      } else if (item.surcharge.type === 'per_person') {
        surcharge = amt * g
        surchargeNote = `+ ₹${amt} × ${g} guests = ₹${surcharge.toLocaleString('en-IN')}`
      }
    }
  }

  const lineExtra = Number(override.lineExtra) || 0
  const profitPct = Number(override.profitPct) || 0
  const subBeforeProfit = baseAmount + surcharge + lineExtra
  const lineProfit = Math.round((subBeforeProfit * profitPct) / 100)
  const total = subBeforeProfit + lineProfit

  return {
    qty,
    unitPrice,
    unitLabel,
    baseAmount,
    surcharge,
    surchargeNote,
    lineExtra,
    profitPct,
    lineProfit,
    total,
  }
}

export const EVENT_TYPES = [
  'Wedding',
  'Engagement',
  'Reception',
  'Birthday',
  'Baby Shower',
  'Corporate',
  'House Warming',
  'Other',
]
